import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FE_Widget } from "@shared/models/integration";
import { ToasterService } from "@shared/services/toaster.service";
import { AdminWidgetEtherpadConfigComponent } from "./etherpad/etherpad.component";
import { AdminWidgetJitsiConfigComponent } from "./jitsi/jitsi.component";
import { AdminIntegrationsApiService } from "@shared/services/admin/admin-integrations-api.service";
import { AdminWidgetWhiteboardConfigComponent } from "./whiteboard/whiteboard.component";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { LogService } from "@shared/services/log.service";
import { SpinnerComponent } from "../../elements/spinner/spinner.component";
import { IboxComponent } from "../../elements/ibox/ibox.component";

import { UiSwitchModule } from "ngx-ui-switch";

export interface WidgetConfigDialogContext {
    widget: FE_Widget;
}

@Component({
    templateUrl: "./widgets.component.html",
    styleUrls: ["./widgets.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SpinnerComponent, IboxComponent, UiSwitchModule, TranslatePipe]
})
export class AdminWidgetsComponent implements OnInit {

    public isLoading = true;
    public isUpdating = false;
    public widgets: FE_Widget[];

    constructor(
        private readonly adminIntegrationsApi: AdminIntegrationsApiService,
        private readonly toaster: ToasterService,
        private readonly modal: NgbModal,
        public translate: TranslateService,
        private readonly logService: LogService,
    ) {
        this.translate = translate;
    }

    ngOnInit() {
        this.adminIntegrationsApi.getAllWidgets().then(widgets => {
            this.isLoading = false;
            this.widgets = widgets;
        }).catch(err => {
            this.logService.error(err);
            this.translate.get('Failed to load widgets').subscribe((res: string) => {
                this.toaster.pop("error", res);
            });
        });
    }

    public toggleWidget(widget: FE_Widget) {
        widget.isEnabled = !widget.isEnabled;
        this.isUpdating = true;
        this.adminIntegrationsApi.toggleIntegration(widget.category, widget.type, widget.isEnabled).then(() => {
            this.isUpdating = false;
            this.translate.get('Widget updated').subscribe((res: string) => {
                this.toaster.pop("success", res);
            });
        }).catch(err => {
            this.logService.error(err);
            widget.isEnabled = !widget.isEnabled; // revert change
            this.isUpdating = false;
            this.translate.get('Error updating widget').subscribe((res: string) => {
                this.toaster.pop("error", res);
            });
        });
    }

    public editWidget(widget: FE_Widget) {
        let component = null;

        if (widget.type === "etherpad") component = AdminWidgetEtherpadConfigComponent;
        if (widget.type === "jitsi") component = AdminWidgetJitsiConfigComponent;
        if (widget.type === "whiteboard") component = AdminWidgetWhiteboardConfigComponent;

        if (!component) {
            this.logService.error("No known dialog component for " + widget.type);
            this.translate.get('Error opening configuration page').subscribe((res: string) => {
                this.toaster.pop("error", res);
            });
            return;
        }

        const widgetConfigRef = this.modal.open(component, {
            backdrop: 'static'
        });
        const widgetConfigInterface = widgetConfigRef.componentInstance as WidgetConfigDialogContext;
        widgetConfigInterface.widget = widget;
    }

    public hasConfiguration(widget: FE_Widget) {
        // Currently only Jitsi and Etherpad have additional configuration
        return widget.type === "jitsi" || widget.type === "etherpad" || widget.type === "whiteboard";
    }
}
